import {
    CheckCircleIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    Squares2X2Icon,
    TruckIcon
} from '@heroicons/react/24/outline';
import { StatCard } from '../StatusCards';
import type {
    PurchaseOrderStage,
    PurchaseOrderStageSummary
} from '../../../services/purchaseOrders';
import {
    derivePurchaseOrderStage,
    purchaseOrderStageFilters,
    type PurchaseOrderStageFilters
} from './poStageFilters';

// Clickable workflow-stage cards; counts come from stageSummary meta, which
// ignores the cards' own filters so switching cards keeps numbers stable.

interface CardConfig {
    stage: PurchaseOrderStage;
    label: string;
    unit: string;
    icon: React.ReactNode;
}

const ICON_CLASS = 'h-3.5 w-3.5';

const CARD_CONFIGS: CardConfig[] = [
    {
        stage: 'all',
        label: 'All purchase orders',
        unit: 'total',
        icon: <Squares2X2Icon className={ICON_CLASS} />
    },
    {
        stage: 'awaitingShipment',
        label: 'Awaiting shipment',
        unit: 'not shipped',
        icon: <TruckIcon className={ICON_CLASS} />
    },
    {
        stage: 'awaitingInvoice',
        label: 'Awaiting invoice',
        unit: 'not billed',
        icon: <DocumentTextIcon className={ICON_CLASS} />
    },
    {
        stage: 'needsAttention',
        label: 'Needs attention',
        unit: 'flagged',
        icon: <ExclamationTriangleIcon className={ICON_CLASS} />
    },
    {
        stage: 'complete',
        label: 'Complete',
        unit: 'shipped + billed',
        icon: <CheckCircleIcon className={ICON_CLASS} />
    }
];

interface PurchaseOrderStatusCardsProps {
    stageSummary: PurchaseOrderStageSummary;
    current: PurchaseOrderStageFilters;
    onChange: (filters: PurchaseOrderStageFilters) => void;
    // Overridable so an embedded host that already sits inside its own page
    // padding isn't double-indented (see InvoiceStatusCards).
    className?: string;
}

export function PurchaseOrderStatusCards({
    stageSummary,
    current,
    onChange,
    className = 'px-8 pt-4'
}: PurchaseOrderStatusCardsProps) {
    const activeStage = derivePurchaseOrderStage(current);

    return (
        <div className={`flex items-stretch gap-3 ${className}`}>
            {CARD_CONFIGS.map((config) => {
                const isActive = activeStage === config.stage;
                return (
                    <StatCard
                        key={config.stage}
                        label={config.label}
                        icon={config.icon}
                        value={String(stageSummary[config.stage]?.count ?? 0)}
                        unit={config.unit}
                        isActive={isActive}
                        onClick={() =>
                            onChange(purchaseOrderStageFilters(isActive ? 'all' : config.stage))
                        }
                    />
                );
            })}
        </div>
    );
}
